import React, { useEffect, useState, useRef, useCallback } from "react";
import Select, { SingleValue, components, MenuListProps } from "react-select";
import { getReq } from "../../utils/request";
import { Organization, OrganizationType } from "../../types/Organization";
import { RatingType } from "../../types/Rating";
import RatingBadge from "../Rating/RatingBadge";
import OrganizationLink from "./OrganizationLink";
import classes from "./organizationPicker.module.scss";

interface OrganizationOption {
    value: Organization;
    label: string;
}

interface OrganizationPickerProps {
    value: Organization | null;
    onChange: (org: Organization | null) => void;
    /**
     * When set, the picker shows rating-aware UI:
     *   - rating badge in each dropdown option
     *   - rating badge next to the selected value
     *   - sort by rating (highest first)
     *   - auto type filter (Provider+Hybrid for PROVIDER, Client+Hybrid for CLIENT)
     *   - freelancer toggle (only when ratingType=PROVIDER)
     *
     * Leave undefined when ratings aren't relevant to the decision (e.g. picking
     * a contact's employer or a parent organization in CRM hierarchy). The picker
     * then renders as a plain rating-free org search.
     */
    ratingType?: RatingType;
    placeholder?: string;
    disabled?: boolean;
    isClearable?: boolean;
    required?: boolean;
    /** When true, shows the open-in-new-tab link next to the selected org. Default true. */
    showOpenLink?: boolean;
    /** Override the search endpoint. Defaults to "organizations/search". */
    searchEndpoint?: string;
    /** Optional org-type filter (single or list) applied client-side after fetch.
     *  Pass [Provider, Hybrid] to behave like provider search, etc. */
    typeFilter?: OrganizationType | OrganizationType[];
    /** Optional id passthrough for label/htmlFor wiring. */
    id?: string;
}

/**
 * Shared organization picker with rating-aware dropdown options + selected display.
 *
 * - Each dropdown option shows: name, reference number, and rating badge
 * - Results are sorted by rating (highest first) within the search match set
 * - Selected value renders the rating badge + an open-in-new-tab link
 * - Designed as a drop-in replacement for the per-page <Select> patterns used
 *   for org search across mission/request/finance/resource forms.
 */
const OrganizationPicker: React.FC<OrganizationPickerProps> = ({
    value,
    onChange,
    ratingType,
    placeholder = "Search organization...",
    disabled = false,
    isClearable = true,
    required = false,
    showOpenLink = true,
    searchEndpoint = "organizations/search",
    typeFilter,
    id,
}) => {
    const [keyword, setKeyword] = useState("");
    const [options, setOptions] = useState<OrganizationOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [freelancerOnly, setFreelancerOnly] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Whether the freelancer toggle makes sense — only relevant for provider picks. */
    const showFreelancerToggle = ratingType === RatingType.PROVIDER;
    /** Whether to render rating badges + apply rating-based sort. Off when caller hasn't opted in. */
    const ratingsEnabled = ratingType !== undefined;

    /** Returns the split rating tuple (operational + financial) appropriate
     *  for the rating type the picker is operating in. Empty when ratings
     *  aren't relevant. */
    const getRatingFor = useCallback(
        (org: Organization): {
            operational: number | null;
            operationalCount: number;
            financial: number | null;
            financialCount: number;
            hideFinancial: boolean;
        } => {
            const o = org as any;
            if (ratingType === RatingType.CLIENT) {
                return {
                    operational: o.operationalClientRating ?? null,
                    operationalCount: o.operationalClientCount ?? 0,
                    financial: o.financialClientRating ?? null,
                    financialCount: o.financialClientCount ?? 0,
                    hideFinancial: !("financialClientRating" in o),
                };
            }
            return {
                operational: o.operationalProviderRating ?? null,
                operationalCount: o.operationalProviderCount ?? 0,
                financial: o.financialProviderRating ?? null,
                financialCount: o.financialProviderCount ?? 0,
                hideFinancial: !("financialProviderRating" in o),
            };
        },
        [ratingType],
    );

    /** Unrated orgs show no badge — a pair of dashes reads as noise in a picker. */
    const ratingFor = (org: Organization) => {
        const r = getRatingFor(org);
        return r.operationalCount > 0 || (!r.hideFinancial && r.financialCount > 0) ? r : null;
    };

    const fetchOptions = useCallback(
        async (q: string) => {
            if (!q || q.trim() === "") {
                setOptions([]);
                return;
            }
            setIsLoading(true);
            try {
                const response = await getReq(`${searchEndpoint}?keyword=${encodeURIComponent(q)}`);
                if (response.status === 200 && Array.isArray(response.body)) {
                    let orgs: Organization[] = response.body;
                    // Default filter derived from ratingType:
                    //   PROVIDER → Provider + Hybrid
                    //   CLIENT   → Client + Hybrid
                    // Explicit typeFilter prop overrides the default when set.
                    const effectiveFilter: OrganizationType[] | null = typeFilter
                        ? Array.isArray(typeFilter) ? typeFilter : [typeFilter]
                        : ratingType === RatingType.CLIENT
                            ? [OrganizationType.Client, OrganizationType.Hybrid]
                            : ratingType === RatingType.PROVIDER
                                ? [OrganizationType.Provider, OrganizationType.Hybrid]
                                : null;
                    if (effectiveFilter) {
                        orgs = orgs.filter((o) => effectiveFilter.includes(o.type));
                    }
                    if (freelancerOnly) {
                        orgs = orgs.filter((o) => (o as any).isFreeLancer === true);
                    }
                    if (ratingType !== undefined) {
                        // Rating-aware sort: highest operational first (mission
                        // planners' primary lens), then by name. Financial isn't
                        // used for sort by default — kept visible in the badge.
                        orgs.sort((a, b) => {
                            const ar = Number(getRatingFor(a).operational ?? -1);
                            const br = Number(getRatingFor(b).operational ?? -1);
                            if (ar !== br) return br - ar;
                            return (a.name || "").localeCompare(b.name || "");
                        });
                    } else {
                        // Plain alphabetical when ratings aren't relevant
                        orgs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                    }
                    setOptions(
                        orgs.map((org) => ({
                            value: org,
                            label: org.name,
                        })),
                    );
                }
            } finally {
                setIsLoading(false);
            }
        },
        [searchEndpoint, typeFilter, ratingType, freelancerOnly, getRatingFor],
    );

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchOptions(keyword);
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [keyword, fetchOptions]);

    const selectedOption: OrganizationOption | null = value
        ? { value, label: value.name }
        : null;

    /** Pick the most relevant reference number to show based on what we're picking. */
    const getReferenceNumber = (org: Organization): string | null => {
        const o = org as any;
        if (ratingType === RatingType.PROVIDER && o.providerReferenceNumber) {
            return o.providerReferenceNumber;
        }
        if (ratingType === RatingType.CLIENT && o.clientReferenceNumber) {
            return o.clientReferenceNumber;
        }
        return o.organizationReferenceNumber ?? null;
    };

    const formatOptionLabel = (option: OrganizationOption, meta: { context: string }) => {
        const org = option.value;
        if (meta.context === "menu") {
            const refNumber = getReferenceNumber(org);
            const isFreelancer = (org as any).isFreeLancer === true;
            return (
                <div className={classes.optionRow}>
                    <div className={classes.optionMain}>
                        <span className={classes.optionName}>
                            {org.name}
                            {ratingsEnabled && isFreelancer && (
                                <span className={classes.freelancerTag} title="Freelancer">
                                    <i className="bi bi-person-badge" /> Freelancer
                                </span>
                            )}
                        </span>
                        {refNumber && (
                            <span className={classes.optionRef}>{refNumber}</span>
                        )}
                    </div>
                    {ratingsEnabled && (() => {
                        const r = ratingFor(org);
                        return r && (
                            <RatingBadge
                                operational={r.operational}
                                operationalCount={r.operationalCount}
                                financial={r.financial}
                                financialCount={r.financialCount}
                                hideFinancial={r.hideFinancial}
                                compact
                            />
                        );
                    })()}
                </div>
            );
        }
        // 'value' context: rendered inside the selected control
        return <span>{org.name}</span>;
    };

    /** Custom MenuList — adds a "Freelancers only" filter row above the options
     *  when picking providers. Toggling it re-runs the search with the new filter. */
    const MenuList = (props: MenuListProps<OrganizationOption>) => (
        <components.MenuList {...props}>
            {showFreelancerToggle && (
                <div
                    className={classes.menuFilterRow}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <label className={classes.menuFilterLabel}>
                        <input
                            type="checkbox"
                            checked={freelancerOnly}
                            onChange={(e) => setFreelancerOnly(e.target.checked)}
                        />
                        Freelancers only
                    </label>
                </div>
            )}
            {props.children}
        </components.MenuList>
    );

    return (
        <div className={classes.pickerWrapper}>
            <div className={classes.pickerControl}>
                <Select<OrganizationOption>
                    aria-label="Select an organization"
                    inputId={id}
                    value={selectedOption}
                    inputValue={keyword}
                    onInputChange={(v, meta) => {
                        // Only react to user typing. On selection / menu close
                        // react-select fires set-value / menu-close — clear our
                        // controlled keyword so the selected label can render
                        // instead of the leftover search term.
                        if (meta.action === "input-change") {
                            setKeyword(v);
                        } else if (
                            meta.action === "set-value" ||
                            meta.action === "menu-close" ||
                            meta.action === "input-blur"
                        ) {
                            setKeyword("");
                        }
                    }}
                    options={options}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    onChange={(opt: SingleValue<OrganizationOption>) => {
                        onChange(opt?.value ?? null);
                        setKeyword(""); // clear search after selection
                    }}
                    noOptionsMessage={({ inputValue }) =>
                        inputValue.trim() === "" ? "Start typing to search" : "No data found"
                    }
                    formatOptionLabel={formatOptionLabel}
                    components={{ MenuList }}
                    className="custom-select"
                    classNamePrefix="custom-select"
                    isDisabled={disabled}
                    isClearable={isClearable}
                    required={required}
                />
            </div>
            {value && (ratingsEnabled || showOpenLink) && (
                <div className={classes.pickerInfoRow}>
                    {ratingsEnabled && (() => {
                        const r = ratingFor(value);
                        return r && (
                            <RatingBadge
                                operational={r.operational}
                                operationalCount={r.operationalCount}
                                financial={r.financial}
                                financialCount={r.financialCount}
                                hideFinancial={r.hideFinancial}
                                compact
                            />
                        );
                    })()}
                    {showOpenLink && <OrganizationLink organization={value} />}
                </div>
            )}
        </div>
    );
};

export default OrganizationPicker;
